import { useEffect, useRef, useState } from "react";
import { fieldHelp, type FieldHelpEntry } from "@/lib/fieldHelp";

/**
 * Small (i) icon button that toggles a positioned popover on click, showing
 * a title/description/example for a form field. Intentionally NOT a native
 * `title`-attribute hover tooltip — it's click-to-open, closes on outside
 * click or Escape, and stays open long enough to read on mobile/touch.
 *
 * Usage: <FieldInfo help="amount" />  (looks up web/src/lib/fieldHelp.ts)
 *     or <FieldInfo content={{ title, description, example }} /> for one-offs.
 */
export function FieldInfo({
  help,
  content,
  label,
}: {
  help?: keyof typeof fieldHelp | string;
  content?: FieldHelpEntry;
  label?: string;
}) {
  const entry = content ?? (help ? fieldHelp[help] : undefined);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!entry) return null;

  return (
    <span className="relative inline-flex align-middle ml-1" ref={ref}>
      <button
        type="button"
        aria-label={`About ${label || entry.title}`}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-muted-foreground/50 text-muted-foreground text-[9px] leading-none hover:border-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
      >
        i
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute z-[70] left-1/2 -translate-x-1/2 top-full mt-1.5 w-64 rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md leading-snug"
        >
          <div className="font-medium mb-1">{entry.title}</div>
          <div className="text-muted-foreground">{entry.description}</div>
          {entry.example && (
            <div className="mt-1.5 text-[11px] text-muted-foreground/80 italic">{entry.example}</div>
          )}
        </div>
      )}
    </span>
  );
}
